/**
 * Money is integer cents everywhere inside the app.
 *
 * LOAD-BEARING: Drizzle returns Postgres `numeric` columns as **strings**, not
 * numbers. Every money value therefore crosses three boundaries as a string
 * ("214.80") and must be converted through here rather than with `Number()`.
 *
 * Why it matters beyond tidiness: the phone shows a live "miles x rate = $"
 * preview computed client-side, and the server recomputes the same value on
 * save. If either side touches a float, the two disagree by a cent on values
 * like 32.05 * 0.715, and that cent later fails the submit-time assertion
 * against `expense_reports.submitted_total_amount` with a 409 on a real,
 * correct submission. Integer cents make that class of bug impossible.
 */

export type Cents = number & { readonly __brand: 'Cents' };

const CENTS_RE = /^-?\d+(\.\d+)?$/;

export function toCents(value: string | number): Cents {
  const s = typeof value === 'number' ? value.toString() : value.trim();
  if (!CENTS_RE.test(s)) throw new Error(`Not a decimal amount: ${JSON.stringify(value)}`);

  const negative = s.startsWith('-');
  const [whole = '0', frac = ''] = (negative ? s.slice(1) : s).split('.');

  // Round half-up on the third decimal rather than truncating: a per-diem
  // component of 0.755 must become 76c, not 75c.
  const centsPart = `${frac}00`.slice(0, 3);
  const scaled = Number(whole) * 1000 + Number(centsPart);
  const rounded = Math.round(scaled / 10);
  return (negative ? -rounded : rounded) as Cents;
}

/** Format for storage in a `numeric` column or for display. Always 2dp. */
export function fromCents(cents: Cents | number): string {
  const n = Math.round(cents);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

export function formatUsd(cents: Cents | number): string {
  const n = Math.round(cents);
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const whole = Math.floor(abs / 100).toLocaleString('en-US');
  return `${sign}$${whole}.${String(abs % 100).padStart(2, '0')}`;
}

export function addCents(...values: Array<Cents | number>): Cents {
  return values.reduce<number>((a, b) => a + Math.round(b), 0) as Cents;
}

/**
 * Multiply a decimal quantity by a decimal rate, returning cents.
 * Both are scaled to integers before multiplying so no float ever holds a
 * money value. Used for miles x rate-per-mile and per-diem prorations.
 */
export function mulRate(quantity: string | number, rate: string | number, rateScale = 4): Cents {
  const q = toScaledInt(quantity, 2);            // 32.05  -> 3205
  const r = toScaledInt(rate, rateScale);        // 0.7150 -> 7150
  // q is scaled 1e2, r is scaled 1e{rateScale}; product is scaled 1e(2+rateScale).
  // Cents want scale 1e2, so divide by 1e{rateScale}, rounding half-up.
  const divisor = 10 ** rateScale;
  return Math.round((q * r) / divisor) as Cents;
}

/** Apply a percentage held as a decimal string ("0.7500") to a cents value. */
export function applyPercent(cents: Cents | number, percent: string | number, scale = 4): Cents {
  const p = toScaledInt(percent, scale);
  const divisor = 10 ** scale;
  return Math.round((Math.round(cents) * p) / divisor) as Cents;
}

function toScaledInt(value: string | number, scale: number): number {
  const s = typeof value === 'number' ? value.toString() : value.trim();
  if (!CENTS_RE.test(s)) throw new Error(`Not a decimal: ${JSON.stringify(value)}`);
  const negative = s.startsWith('-');
  const [whole = '0', frac = ''] = (negative ? s.slice(1) : s).split('.');
  const padded = `${frac}${'0'.repeat(scale + 1)}`.slice(0, scale + 1);
  const scaled = Number(whole) * 10 ** (scale + 1) + Number(padded);
  const rounded = Math.round(scaled / 10);
  return negative ? -rounded : rounded;
}
